import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle, ArrowLeft } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import CallModal from "../../pages/Lead/AddCall";
import EditCallModal from "../../pages/Lead/EditCall";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/CallLead/CallTablestyle";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

// Define API response structure
interface APICall {
  call_id: number;
  subject: string;
  status_1: string;
  status_2: string;
  call_description: string;
  lead: string;
  call_date: string;
  duration: string;
}

// Define Call structure for frontend
interface Call {
  call_id: number;
  subject: string;
  status_1: string;
  status_2: string;
  call_description: string;
  lead: string;
  call_date: string;
  duration: string;
}

const CallTable: React.FC = () => {
  const { lead_id } = useParams<{ lead_id: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [calls, setCalls] = useState<Call[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Call | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedCallId, setSelectedCallId] = useState<number | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showEditCallModal, setShowEditCallModal] = useState(false);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  // State to store lead name
  const [leadName, setLeadName] = useState<string>("");

  // Callback for when a call is updated in the EditCallModal
  const handleCallUpdated = () => {
    fetchCalls();
    setShowEditCallModal(false);
  };

  const handleCallAdded = () => {
    fetchCalls(); // Refetch calls to update the table
  };

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  const handleDeleteClick = (callId: number) => {
    setSelectedCallId(callId);
    openModal();
  };

  const handleEditClick = (callId: number) => {
    setSelectedCallId(callId);
    setShowEditCallModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCallId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/call/delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ call_id: selectedCallId }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 200) {
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        setCalls((prevCalls) => {
          const updatedCalls = prevCalls.filter(
            (call) => call.call_id !== selectedCallId
          );
          if (updatedCalls.length > 0 && displayedCalls.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedCalls;
        });
        closeModal();
      } else {
        toast.error(result.message || "Failed to delete call.");
      }
    } catch (error) {
      console.error("Network error:", error);
      toast.error("An unexpected error occurred while deleting the call.");
    }
  };

  // Memoize fetchCalls to prevent unnecessary re-renders
  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/call/search/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            lead_id: lead_id,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 200) {
        const formattedCalls: Call[] = Array.isArray(result.data)
          ? result.data.map((call: APICall) => ({
            call_id: call.call_id,
            subject: call.subject,
            status_1: call.status_1,
            status_2: call.status_2,
            call_description: call.call_description,
            lead: call.lead,
            call_date: call.call_date,
            duration: call.duration,
          }))
          : [];
        setCalls(formattedCalls);
        // Set lead name from the first call if available
        setLeadName(formattedCalls.length > 0 ? formattedCalls[0].lead : "Unknown Lead");
      } else {
        setCalls([]);
        setLeadName("Unknown Lead");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setCalls([]);
      setLeadName("Unknown Lead");
    } finally {
      setLoading(false);
    }
  }, [lead_id]);

  // Filter and sort Calls
  const filteredCalls = calls.filter((call) =>
    Object.values(call)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedCalls = [...filteredCalls].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
  });

  const displayedCalls = sortedCalls.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredCalls.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Call) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredCalls
      : sortColumn
        ? sortedCalls
        : calls;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((call) => ({
      Subject: call.subject,
      "Call Date & Time": call.call_date,
      "Status 1": call.status_1,
      "Status 2": call.status_2,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [[`Call Data List for ${leadName}`]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          `Call Data List for ${leadName}`.length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Calls");
    XLSX.writeFile(
      workbook,
      searchTerm ? `CallData_${searchTerm}.xlsx` : `CallData_${leadName}.xlsx`
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Call List For {leadName}</h3>
          <Link
            to="/lead-list"
            className={styles.back}
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.headerControls}>
            <RowsPerPageSelector
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
            <div className={styles.controlsInner}>
              <div className={styles.searchContainer}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className={styles.downloadButton}
                onClick={handleDownload}
              >
                Download
                <DownloadIcon className={styles.downloadIcon} />
              </button>
              <button
                onClick={() => setShowCallModal(true)}
                className={styles.addButton}
              >
                Add New Call
                <PlusIcon />
              </button>
              <CallModal
                state={{ lead_id: lead_id }}
                isOpen={showCallModal}
                onClose={() => setShowCallModal(false)}
                onCallAdded={handleCallAdded}
              />
            </div>
          </div>
          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.theadRow}>
                  <th
                    className={styles.thName}
                    onClick={() => handleSort("subject")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Subject</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("call_date")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Call Date & Time</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("status_1")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Status 1</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("status_2")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Status 2</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th className={styles.thAction}>
                    <p className={styles.thText}>Action</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={8} className="text-center py-4">
                      <Loader />
                    </td>
                  </tr>
                ) : calls.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={5} className={styles.noDataText}>
                      Your call list is currently empty. Use the ‘Add New Call’ button to create one.
                    </td>
                  </tr>
                ) : filteredCalls.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={5} className={styles.noDataText}>
                      No records match your search.
                    </td>
                  </tr>
                ) : (
                  displayedCalls.map((call) => (
                    <tr key={call.call_id} className={styles.tbodyRow}>
                      <td className={styles.tdName}>{call.subject}</td>
                      <td className={styles.tdRole}>{call.call_date}</td>
                      <td className={styles.tdRole}>{call.status_1}</td>
                      <td className={styles.tdRole}>{call.status_2}</td>
                      <td className={styles.tdAction}>
                        <div className={styles.actionContainer}>
                          <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                            <button
                              onClick={() => handleEditClick(call.call_id)}
                              className={styles.editButton}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                          {selectedCallId === call.call_id && (
                            <EditCallModal
                              state={{
                                lead_id: lead_id || "",
                                call_id: selectedCallId.toString(),
                              }}
                              isOpen={showEditCallModal}
                              onClose={() => setShowEditCallModal(false)}
                              onCallUpdated={handleCallUpdated}
                            />
                          )}
                          <Tooltip content="Delete" bgColor="bg-red-600">
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDeleteClick(call.call_id)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            filteredLength={filteredCalls.length}
            handlePageChange={handlePageChange}
          />
        </div>

        <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
          <button
            onClick={closeModal}
            className={styles.modalCloseButton}
          ></button>
          <div className="flex justify-center">
            <div className={styles.modalIconContainer}>
              <FontAwesomeIcon icon={faTrash} className={styles.modalIcon} />
            </div>
          </div>
          <h4 className={styles.modalTitle}>Confirm Delete</h4>
          <p className={styles.modalText}>
            Are you sure you want to delete this call?
          </p>
          <div className={styles.modalButtonContainer}>
            <button onClick={closeModal} className={styles.modalCancelButton}>
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={isDeleteSuccessModalOpen}
          onClose={() => setDeleteSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-800 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
            Call Deleted Successfully!
          </h4>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setDeleteSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default CallTable;